/**
 * Which event types show on the timeline. Lifecycle events
 * (session.started, step.started, session.ended) are always shown.
 *
 * Each chip is a real checkbox with the input visually hidden, so the bar
 * stays keyboard- and screen-reader-navigable while reading as a row of
 * toggles rather than as a form.
 */
import type { ReplayEventType } from '../../api/replay';
import { cn } from '@/lib/utils';
import { EVENT_STYLES, FILTERABLE_TYPES, toneClass } from './eventStyles';

export type Filters = Record<ReplayEventType, boolean>;

export function makeDefaultFilters(): Filters {
  // True for every type — lifecycle events aren't toggleable but it's
  // easier to set them true here than to special-case them everywhere.
  return Object.fromEntries(
    (Object.keys(EVENT_STYLES) as ReplayEventType[]).map((t) => [t, true]),
  ) as Filters;
}

interface Props {
  filters: Filters;
  onChange: (filters: Filters) => void;
  /** How many events of each type the session holds, for the chip counts. */
  counts?: Partial<Record<ReplayEventType, number>>;
}

export function ReplayFilters({ filters, onChange, counts }: Props) {
  function toggle(type: ReplayEventType): void {
    onChange({ ...filters, [type]: !filters[type] });
  }

  const allOn = FILTERABLE_TYPES.every((t) => filters[t]);

  return (
    <div role="group" aria-label="Event type filters" className="flex flex-wrap items-center gap-1.5">
      {FILTERABLE_TYPES.map((type) => {
        const style = EVENT_STYLES[type];
        const checked = filters[type];
        const n = counts?.[type];
        return (
          <label
            key={type}
            data-testid={`replay-filter-${type}`}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-meta font-medium transition-colors select-none',
              toneClass(type),
              checked
                ? 'border-ev/40 bg-ev/10 text-ink'
                : 'border-line text-ink-faint hover:text-ink-dim',
            )}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(type)}
              aria-label={`Toggle ${style.label}`}
              className="sr-only"
            />
            <span
              aria-hidden
              className={cn('size-2 rounded-full', checked ? 'bg-ev' : 'bg-current opacity-50')}
            />
            {style.label}
            {n !== undefined ? <span className="tabular-nums opacity-60">{n}</span> : null}
          </label>
        );
      })}
      <button
        type="button"
        onClick={() =>
          onChange({
            ...filters,
            ...(Object.fromEntries(FILTERABLE_TYPES.map((t) => [t, !allOn])) as Filters),
          })
        }
        className="rounded-full px-2 py-1 text-meta font-medium text-ink-faint transition-colors hover:text-ink"
      >
        {allOn ? 'Hide all' : 'Show all'}
      </button>
    </div>
  );
}
